// Validation for the Housing Options table's "Height" column (the BURNER_TYPE
// DIMENSIONS cell). That value encodes through the finite DIMENSIONS_HEIGHT
// token map (see the MAPPING op / cpq2-mapping-encoder.js): a value the encoder
// can't find there is written as the null token and silently dropped from the
// item. So the widget lets the user pick OR type a height, flags an unmapped
// value inline, and Save & Submit hard-blocks on one (see SaveSubmitButton).
//
// The valid set is read from the encoder's own spec at runtime (never drifts),
// and validity uses the SAME normalizeMixedInches the encoder uses, so what we
// accept is exactly what the encoder can map.

import type { AnswerState } from "../types";
import type { MappingSpec } from "./mappingDecoder";
import { normalizeMixedInches } from "./mappingDecoder";

// Answer keys the Housing Options Height cells write to (one per table row).
const DIMENSIONS_KEY_RE = /^BURNER_TYPE\/DIMENSIONS_\d+$/;

/** The finite set of housing heights the encoder can map (DIMENSIONS_HEIGHT
 *  token map), or null when the mapping spec can't be read — callers then treat
 *  every value as acceptable so a parse failure never blocks. */
export function housingHeightKeys(spec: MappingSpec | null): ReadonlySet<string> | null {
  const m = spec?.TOKEN_MAPS?.DIMENSIONS_HEIGHT;
  return m ? new Set(Object.keys(m)) : null;
}

/** True when a Height value maps — or the set is unknown, or it's empty / None
 *  (an empty height isn't an "invalid" height; that's a separate concern). */
export function isMappedHeight(v: string, keys: ReadonlySet<string> | null): boolean {
  if (!keys) return true;
  const s = String(v ?? "").trim();
  if (s === "" || /^none$/i.test(s)) return true;
  const norm = normalizeMixedInches(s, 16);
  return norm != null && keys.has(String(norm).trim());
}

/** The first user-entered Housing Height with no mapping, or null when all are
 *  fine. Only inspects the DIMENSIONS answer keys (user edits); generated row
 *  values come from the mapped qtable, so they never trip this. */
export function findInvalidHousingHeight(
  answers: AnswerState,
  keys: ReadonlySet<string> | null
): { key: string; value: string } | null {
  if (!keys) return null;
  for (const k of Object.keys(answers)) {
    if (!DIMENSIONS_KEY_RE.test(k)) continue;
    const raw = answers[k];
    const s = raw == null ? "" : String(raw);
    if (s.trim() === "") continue;
    if (!isMappedHeight(s, keys)) return { key: k, value: s };
  }
  return null;
}
